/**
 * Whether a board was last seen to have chat conversations.
 *
 * The board blanks its chat slot — panel AND collapsed button — until a
 * `getConversationsByContext` call answers, so that it can choose between the
 * entry card and the docked chat without flashing both. That wait is only
 * defensible the first time a board is opened. On a reopen the answer is
 * already known, and waiting again is what surfaced as "the chat box in the
 * right bottom corner disappeared and came back after some time".
 *
 * Remembered per board, per browser. A wrong value costs one frame — the
 * request that follows corrects it — so it is deliberately not treated as
 * authoritative anywhere else.
 */
const KEY = (boardId: string) => `board-has-conversations:${boardId}`

/** `null` when this board has not been opened in this browser before. */
export function readRememberedConversations(boardId: string): boolean | null {
  try {
    const raw = localStorage.getItem(KEY(boardId))
    return raw === null ? null : raw === '1'
  } catch {
    // Private mode, or storage disabled. Fall back to the first-open path.
    return null
  }
}

export function rememberConversations(boardId: string, hasConversations: boolean): void {
  try {
    localStorage.setItem(KEY(boardId), hasConversations ? '1' : '0')
  } catch {
    // Not remembering is the status quo, never an error worth surfacing.
  }
}
